import { maybe, Maybe } from '@passionware/monads';
import { omit, partial, partialRight, pickBy } from 'lodash';
import { Nullable } from '../../../platform/typescript';
import { Pagination } from './pagination';
import { Sorter } from './sorters/Sorter';

export interface WithSorter<Field> {
  sort: Nullable<Sorter<Field>>;
}

export type WithPagination = {
  page: Pagination;
};

export type WithSearch = {
  search: string;
};

type FilterBase = Record<string, unknown>;
export type WithFilters<F extends FilterBase> = {
  filters: { [K in keyof F]: Nullable<F[K]> };
};

export type WithOptionalFilters<F extends FilterBase> = {
  filters: { [K in keyof F]?: Maybe<F[K]> };
};

export type TransformOptions = {
  preservePage?: boolean;
};

export function normalizeQuery<Q extends WithFilters<FilterBase>>(query: Q): Q {
  return pickBy(
    { ...query, filters: pickBy(query.filters, maybe.isPresent) },
    maybe.isPresent
  ) as Q;
}

const resetPageIfNeeded = <Q extends WithPagination>(
  query: Q,
  options?: TransformOptions
): Q =>
  options?.preservePage ? query : withPaginationUtils<Q>().resetPage(query);

export const withPaginationUtils = <Q extends WithPagination>() => ({
  resetPage: (query: Q): Q => ({
    ...query,
    page: {
      ...query.page,
      page: 0
    }
  }),
  nextPage: (query: Q): Q => ({
    ...query,
    page: {
      ...query.page,
      page: query.page.page + 1
    }
  }),
  prevPage: (query: Q): Q => {
    if (query.page.page === 0) {
      throw new Error(
        'Cannot go to previous page when already on the first page'
      );
    }
    return {
      ...query,
      page: {
        ...query.page,
        page: query.page.page - 1
      }
    };
  },
  setPage: (query: Q, page: Pagination): Q => ({
    ...query,
    page
  }),
  setPageSize: (query: Q, pageSize: number): Q => ({
    ...query,
    page: {
      page: 0, // reset page to 0 due to new page size
      pageSize
    }
  }),
  withPage: (page: Pagination) =>
    partialRight(withPaginationUtils<Q>().setPage, page)
});
export const withSorterUtils = <
  Q extends { sort: Maybe<SpecificSorter> } & WithPagination,
  SpecificSorter extends Sorter<unknown> = Sorter<unknown>
>() => {
  const api = {
    setSort: (
      query: Q,
      sort: Maybe<Q['sort']>,
      options?: TransformOptions
    ): Q => resetPageIfNeeded({ ...query, sort }, options),
    removeSort: (query: Q, options?: TransformOptions): Q =>
      resetPageIfNeeded({ ...query, sort: maybe.ofAbsent() }, options),
    withSort: (sort: Maybe<Q['sort']>, options?: TransformOptions) =>
      partialRight(api.setSort, sort, options)
  };
  return api;
};
export const withSearchUtils = <Q extends WithSearch & WithPagination>() => {
  const api = {
    setSearch: (
      query: Q,
      search: Maybe<string>,
      options?: TransformOptions
    ): Q =>
      resetPageIfNeeded(
        maybe.mapOrElse(
          search,
          search => ({
            ...query,
            search: search.trimStart()
          }),
          query
        ),
        options
      ),
    removeSearch: (query: Q): Q => ({
      ...query,
      search: ''
    }),
    withSearch: (search: Maybe<string>, options?: TransformOptions) =>
      partialRight(api.setSearch, search, options)
  };
  return api;
};

export const withFiltersUtils = <
  Q extends WithFilters<FilterBase> & WithPagination
>() => {
  const api = {
    setFilter: <K extends keyof Q['filters']>(
      query: Q,
      filterName: K,
      value: Maybe<Q['filters'][K]>,
      options?: TransformOptions
    ): Q =>
      resetPageIfNeeded(
        {
          ...query,
          filters: {
            ...query.filters,
            [filterName]: maybe.of(value)
          }
        },
        options
      ),
    replaceFilters: (
      query: Q,
      filters: Q['filters'],
      options?: TransformOptions
    ): Q =>
      resetPageIfNeeded(
        {
          ...query,
          filters
        },
        options
      ),
    withReplacedFilters: (filters: Q['filters'], options?: TransformOptions) =>
      partialRight(api.replaceFilters, filters, options),
    removeFilter: (
      query: Q,
      filterName: keyof Q['filters'],
      options?: TransformOptions
    ): Q =>
      resetPageIfNeeded(
        {
          ...query,
          filters: {
            ...query.filters,
            [filterName]: maybe.ofAbsent()
          }
        },
        options
      ),
    removeOptionalFilter: (
      query: Q,
      filterName: keyof Q['filters'],
      options?: TransformOptions
    ): Q =>
      resetPageIfNeeded(
        {
          ...query,
          filters: omit(query.filters, filterName)
        },
        options
      ),
    withFilter: <K extends keyof Q['filters']>(
      filterName: K,
      value: Maybe<Q['filters'][K]>,
      options?: TransformOptions
    ) => partialRight(api.setFilter, filterName, value, options),
    withoutFilter: (
      filterName: keyof Q['filters'],
      options?: TransformOptions
    ) => partialRight(api.removeFilter, filterName, options),
    withoutOptionalFilter: (
      filterName: keyof Q['filters'],
      options?: TransformOptions
    ) => partialRight(api.removeOptionalFilter, filterName, options),
    mergeFilters: (
      query: Q,
      payload: Partial<{ [K in keyof Q['filters']]: Nullable<Q['filters'][K]> }>
    ): Q => {
      const mergedFilters: Q['filters'] = { ...query.filters };

      for (const key in payload) {
        if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;
        const val = payload[key];
        // Apply update only if explicitly null or present (not undefined)
        if (val !== undefined) {
          mergedFilters[key as keyof Q['filters']] =
            val as Q['filters'][keyof Q['filters']];
        }
        // if undefined or absent, don't change that filter
      }

      return {
        ...query,
        filters: mergedFilters
      };
    },
    withMergedFilters: (
      payload: Partial<{
        [K in keyof Q['filters']]: Nullable<Q['filters'][K]>;
      }>
    ) => partialRight(api.mergeFilters, payload),
    /**
     * Checks if the query is narrowing down the results (has active filters or search).
     * @param query - The query to check.
     * @param excludeFilters - Optional array of filter keys to exclude from the check.
     * @returns True if the query is narrowing down the results, false otherwise.
     */
    isNarrowing: (query: Q, excludeFilters: (keyof Q['filters'])[] = []) => {
      const hasActiveFilters = Object.entries(query.filters).some(
        ([key, value]) =>
          !excludeFilters.includes(key as keyof Q['filters']) &&
          maybe.isPresent(value)
      );
      const hasSearch =
        'search' in query && maybe.isPresent(maybe.fromTruthy(query.search));
      return hasActiveFilters || hasSearch;
    },
    /**
     * Clears all narrowing criteria (filters and search), keeping excluded filters intact.
     * @param query - The query to clear.
     * @param excludeFilters - Optional array of filter keys to preserve.
     * @returns Query with narrowing cleared.
     */
    clearNarrowing: (
      query: Q,
      excludeFilters: (keyof Q['filters'])[] = []
    ): Q => {
      const clearedFilters = Object.fromEntries(
        Object.entries(query.filters).map(([key, value]) => [
          key,
          excludeFilters.includes(key as keyof Q['filters']) ? value : null
        ])
      ) as Q['filters'];

      return {
        ...query,
        filters: clearedFilters,
        ...('search' in query ? { search: '' } : {})
      };
    }
  };
  return api;
};

export const withBuilderUtils = <Api>(api: Api) => {
  return {
    setInitialQueryFactory: <Input extends unknown[], Q>(
      factoryFn: (api: Api) => (...args: Input) => Q
    ) => {
      const rawBuild = (
        startQuery: Q,
        getMappers: (
          api: Api & { unchanged: () => (query: Q) => Q }
        ) => Array<(input: Q) => Q>
      ): Q => {
        const api2 = { ...api, unchanged: () => (query: Q) => query };
        return getMappers(api2).reduce(
          (query, mapper) => mapper(query),
          startQuery
        );
      };

      const initialQueryFactory = factoryFn(api);

      return {
        ...api,
        getBuilder: (...args: Input) => {
          // Explicitly infer the types for slicing

          const initialQuery = initialQueryFactory(...args);
          return {
            build: partial(rawBuild, initialQuery)
          };
        },
        transform: (initialQuery: Q) => ({
          build: partial(rawBuild, initialQuery)
        })
      };
    }
  };
};

export const searchUtils = {
  matches: (query: WithSearch | string, value: string) => {
    const searchee = typeof query === 'string' ? query : query.search;
    return value.toLowerCase().includes(searchee.toLowerCase());
  }
};
